import { createStore, createEvent } from "effector";

// Events to update the queue
export const addToQueue = createEvent();
export const removeFromQueue = createEvent();
export const updateGuest = createEvent(); // 🔹 New event to update a guest

// Load guests from localStorage when the page loads
const savedGuests = JSON.parse(localStorage.getItem("guestQueue")) || [];

// Store for queue data
export const $queue = createStore(savedGuests);
$queue
  .on(addToQueue, (state, newGuest) => {
    const exists = state.some((guest) => guest.id === newGuest.id);
    if (exists) {
      console.warn("⚠️ Duplicate guest detected, skipping:", newGuest);
      return state;
    }

    // Add timestamp when the guest is added
    const guestWithTimestamp = {
      ...newGuest,
      timestamp: Date.now(), // Store current time
    };

    const updatedQueue = [guestWithTimestamp, ...state];

    localStorage.setItem("guestQueue", JSON.stringify(updatedQueue));

    console.log("✅ Guest added to queue:", guestWithTimestamp);
    return updatedQueue;
  })

  .on(removeFromQueue, (state, id) => {
    const updatedQueue = state.filter((guest) => guest.id !== id);

    // 🔹 Save the updated queue to localStorage
    localStorage.setItem("guestQueue", JSON.stringify(updatedQueue));

    console.log("✅ Guest removed:", id);
    return [...updatedQueue]; // Ensure a new array is returned for reactivity
  })
  .on(updateGuest, (state, updatedGuest) => {
    const updatedQueue = state.map((guest) =>
      guest.id === updatedGuest.id || guest.id === updatedGuest.user_id
        ? { ...guest, ...updatedGuest } // 🔹 Create a completely new object
        : guest
    );

    // console.log("🔄 Store updated with:", updatedQueue); // Debugging

    return [...updatedQueue]; // 🔹 Ensures React detects change
  });

export const moveToSeated = createEvent();

const savedSeatedGuests =
  JSON.parse(localStorage.getItem("seatedGuests")) || [];

export const $seatedGuests = createStore(savedSeatedGuests).on(
  moveToSeated,
  (state, guest) => {
    if (state.some((g) => g.id === guest.id)) {
      console.warn("⚠️ Duplicate guest detected, skipping:", guest);
      return state;
    }

    const guestWithTime = {
      ...guest,
      seatedAt: Date.now(),
    };

    const updatedSeated = [...state, guestWithTime].sort((a, b) => {
      const now = Date.now();
      const aTimeLeft = a.seatedAt + 300000 - now;
      const bTimeLeft = b.seatedAt + 300000 - now;

      // Sort logic: Expired (left) → Orange → Most Time (right)
      if (aTimeLeft <= 0 && bTimeLeft > 0) return -1;
      if (bTimeLeft <= 0 && aTimeLeft > 0) return 1;
      return aTimeLeft - bTimeLeft;
    });

    localStorage.setItem("seatedGuests", JSON.stringify(updatedSeated));

    return updatedSeated;
  }
);

export const removeFromSeated = createEvent(); // New event

$seatedGuests.on(removeFromSeated, (state, id) => {
  const updatedList = state.filter((guest) => guest.id !== id);
  localStorage.setItem("seatedGuests", JSON.stringify(updatedList));
  return updatedList; // Ensure state updates
});

// 🔥 Function to remove guests if they are not seated after 1 hour
const removeOldGuests = () => {
  const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
  const now = Date.now();

  $queue.getState().forEach((guest) => {
    if (now - guest.timestamp > oneHour) {
      removeFromQueue(guest.id);
      console.log(`⏳ Guest ${guest.id} removed after 1 hour in queue`);
    }
  });
};

// 🔄 Run cleanup every 1 minute
setInterval(removeOldGuests, 60 * 1000);

export const reorderSeatedGuests = () => {
  const now = Date.now();
  const seatedGuests = $seatedGuests.getState().map((guest) => ({
    ...guest,
    timeLeft: guest.seatedAt + 300000 - now,
  }));

  // Sorting logic: Expired (left) → Orange → Most Time (right)
  const sortedGuests = seatedGuests.sort((a, b) => {
    if (a.timeLeft <= 0 && b.timeLeft > 0) return -1;
    if (b.timeLeft <= 0 && a.timeLeft > 0) return 1;
    return a.timeLeft - b.timeLeft;
  });

  localStorage.setItem("seatedGuests", JSON.stringify(sortedGuests));

  // 🔹 Update the Effector store so UI re-renders
  $seatedGuests.setState(sortedGuests);
};
